import React, { useCallback, useEffect, useState } from "react";
import DataTable from "react-data-table-component";
import { Button, Toast, ToastContainer } from "react-bootstrap";
import NotificationComposerModal from "./components/NotificationComposerModal";
import { getNotificationHistory } from "../services/apiService";

const pageBackground = {
  minHeight: "100vh",
  background: "#f5f7fb",
  padding: "2.5rem 2rem",
  color: "#0f172a",
};

const panelStyle = {
  background: "#ffffff",
  border: "1px solid #e5e7eb",
  boxShadow: "0 20px 50px rgba(15, 23, 42, 0.08)",
  borderRadius: "16px",
};

const formatDate = (value) => {
  if (!value) return "-";
  try {
    return new Date(value).toLocaleString();
  } catch {
    return value;
  }
};

const renderTarget = (row) => {
  if (row.send_to_all) return ["All users"];
  if (row.profession_categories && row.profession_categories.length > 0) {
    return row.profession_categories;
  }
  return [];
};

export default function NotificationBroadcaster() {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [showModal, setShowModal] = useState(false);
  const [toastInfo, setToastInfo] = useState({ show: false, message: "", variant: "success" });

  const pageSize = 10;

  const loadNotifications = useCallback(
    async (pageToLoad = 1) => {
      if (!process.env.REACT_APP_API_BASE_URL) {
        setError("API base URL is not configured in .env");
        setLoading(false);
        return;
      }

      setError(null);
      setLoading(true);
      try {
        const response = await getNotificationHistory(pageToLoad, pageSize);
        const items = response?.data?.notifications || [];
        setRecords(items);
        setPage(response?.data?.page || pageToLoad);
        setTotalPages(response?.data?.total_pages || 1);
        setTotalRecords(response?.data?.total || items.length);
      } catch (e) {
        console.error(e);
        setError("Failed to load notifications.");
      } finally {
        setLoading(false);
      }
    },
    [pageSize]
  );

  useEffect(() => {
    loadNotifications();
  }, [loadNotifications]);

  const columns = [
    { name: "#", selector: (_row, index) => (page - 1) * pageSize + index + 1, width: "60px" },
    { name: "Title", selector: (row) => row.title, wrap: true, width: "220px" },
    { name: "Message", selector: (row) => row.body, wrap: true, grow: 2 },
    {
      name: "Send To",
      selector: (row) => renderTarget(row),
      wrap: true,
      width: "200px",
      cell: (row) => (
        <div className="d-flex flex-wrap gap-2">
          {renderTarget(row).map((item) => (
            <span
              key={item}
              className="badge bg-info text-dark"
              style={{ fontSize: "0.7rem" }}
            >
              {item}
            </span>
          ))}
          {renderTarget(row).length === 0 && <span className="text-muted">-</span>}
        </div>
      ),
    },
    {
      name: "Total",
      selector: (row) => row.total_tokens || 0,
      wrap: true,
      width: "100px",
    },
    {
      name: "Date",
      selector: (row) => row.created_at,
      wrap: true,
      width: "180px",
      cell: (row) => formatDate(row.created_at),
    },
  ];

  return (
    <div style={pageBackground}>
      <div style={{ width: "100%", maxWidth: "1400px", margin: "0 auto" }}>
       

        <div style={{ ...panelStyle, padding: "1.25rem" }}>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <div>
              <h3 className="mb-0">Broadcast history</h3>
              <small className="text-muted">
                Review what was sent, who received it, and when.
              </small>
            </div>
            <Button
              variant="outline-primary"
              onClick={() => loadNotifications(1)}
              disabled={loading}
            >
              Refresh
            </Button>
          </div>

          {error && <div className="alert alert-danger mb-3">{error}</div>}

          <DataTable
            columns={columns}
            data={records}
            highlightOnHover
            dense
            fixedHeader
            fixedHeaderScrollHeight="calc(100vh - 260px)"
            progressPending={loading}
            noDataComponent={<div className="text-muted py-3">No notifications found.</div>}
            style={{ fontSize: "0.78rem" }}
            customStyles={{
              table: { style: { tableLayout: "fixed", backgroundColor: "transparent" } },
              header: { style: { backgroundColor: "transparent", color: "#0f172a" } },
              headRow: { style: { backgroundColor: "#f8fafc", borderBottomColor: "#e5e7eb" } },
              headCells: { style: { color: "#0f172a", fontWeight: 700 } },
              rows: {
                style: { backgroundColor: "#ffffff", borderBottomColor: "#e5e7eb", color: "#0f172a" },
                highlightOnHoverStyle: { backgroundColor: "#f1f5f9", color: "#0f172a" },
              },
            }}
          />

          <div className="d-flex justify-content-between align-items-center mt-3 flex-wrap gap-2">
            <div className="text-muted">
              Page {page} of {totalPages} &middot; {totalRecords} total
            </div>
            <div className="d-flex gap-2">
              <Button
                variant="outline-secondary"
                onClick={() => loadNotifications(Math.max(1, page - 1))}
                disabled={loading || page <= 1}
              >
                Previous
              </Button>
              <Button
                variant="primary"
                onClick={() => loadNotifications(Math.min(totalPages, page + 1))}
                disabled={loading || page >= totalPages}
                style={{
                  background: "linear-gradient(135deg, #2563eb, #22c55e)",
                  border: "none",
                  fontWeight: 700,
                  color: "#0b1220",
                }}
              >
                Next
              </Button>
            </div>
          </div>
        </div>
      </div>

      <button
        type="button"
        className="btn btn-primary"
        onClick={() => setShowModal(true)}
        style={{
          position: "fixed",
          right: "28px",
          bottom: "90px",
          borderRadius: "50%",
          width: "64px",
          height: "64px",
          fontSize: "1.5rem",
          padding: 0,
          background: "linear-gradient(135deg, #2563eb, #22c55e)",
          border: "none",
          boxShadow: "0 12px 30px rgba(37, 99, 235, 0.35)",
        }}
      >
        +
      </button>

      {showModal && (
        <NotificationComposerModal
          show={showModal}
          allowTargetSelection
          onClose={() => setShowModal(false)}
          onSuccess={(message, variant = "success") => {
            setToastInfo({ show: true, message, variant });
            loadNotifications(1);
            setShowModal(false);
          }}
        />
      )}

      <ToastContainer position="bottom-end" className="p-3">
        <Toast
          bg={toastInfo.variant}
          onClose={() => setToastInfo({ ...toastInfo, show: false })}
          show={toastInfo.show}
          delay={3000}
          autohide
        >
          <Toast.Body className="text-white">{toastInfo.message}</Toast.Body>
        </Toast>
      </ToastContainer>
    </div>
  );
}
